import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

export interface UseCancelInterviewOptions {
  additionalInvalidateKeys?: string[][];
}

export function useCancelInterview(options: UseCancelInterviewOptions = {}) {
  const { toast } = useToast();
  const [cancelInterviewId, setCancelInterviewId] = useState<string | null>(null);
  const [isGroupInterview, setIsGroupInterview] = useState(false);

  const mutation = useMutation({
    mutationFn: async (interviewId: string) => {
      return rpcClient.interviews.delete({ id: interviewId });
    },
    onSuccess: () => {
      toast({
        title: "Entrevista cancelada",
        description: "A entrevista foi excluída. O candidato receberá um email de cancelamento.",
      });
      queryClient.invalidateQueries({ queryKey: orpc.interviews.mine.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      for (const key of options.additionalInvalidateKeys || []) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      setCancelInterviewId(null);
      setIsGroupInterview(false);
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao cancelar", description: error.message, variant: "destructive" });
    },
  });

  const openCancel = (interviewId: string, groupInterview = false) => {
    setCancelInterviewId(interviewId);
    setIsGroupInterview(groupInterview);
  };

  const closeCancel = () => {
    setCancelInterviewId(null);
    setIsGroupInterview(false);
  };

  const confirmCancel = () => {
    if (cancelInterviewId) {
      mutation.mutate(cancelInterviewId);
    }
  };

  return {
    cancelInterviewId,
    isGroupInterview,
    isPending: mutation.isPending,
    openCancel,
    closeCancel,
    confirmCancel,
  };
}
